import React, { useState } from 'react';
import { CamelizedMediaEntry, MediaEntry } from 'src/types/global-types';
import { ModalHeader } from 'src/components/ProductDetail/ModalHeader';
import { ProductGallery, ProductGalleryProvider } from 'src/components/ProductDetail/ProductGallery';
import { Modal } from 'src/components/Modal/Modal';
import snakecaseKeys from 'snakecase-keys';

import classes from './MixAndMatchImageGallery.scss';

type Props = {
    url: string;
    isProductPicked: boolean;
    productMedia?: CamelizedMediaEntry[];
    productName: string;
    is24HourDelivery: boolean;
    canCustomise: boolean;
    additionalMediaEntry?: MediaEntry;
};

export const updateMediaEntries = <T extends object>(mediaEntries: T[] = []): T[] =>
    mediaEntries.map((item) => {
        if ('media_type' in item && item.media_type) return item;
        if ('mediaType' in item && item.mediaType) return item;
        return { ...item, media_type: 'image' };
    }) as T[];

export const MixAndMatchImageGallery: React.FC<Props> = ({
    url,
    isProductPicked,
    productMedia,
    productName,
    is24HourDelivery,
    canCustomise,
    additionalMediaEntry,
}) => {
    const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
    const isGalleryAvailable = Boolean(isProductPicked && productMedia && productMedia.length > 0);

    const mediaWithType = isGalleryAvailable ? updateMediaEntries(productMedia ?? []) : productMedia;
    const snakeCaseProductMedia = snakecaseKeys((mediaWithType ?? []) as any) as unknown as MediaEntry[];

    const onDismiss = () => setIsGalleryModalOpen(false);
    const displayedMedia = additionalMediaEntry
        ? [additionalMediaEntry, ...snakeCaseProductMedia]
        : snakeCaseProductMedia;

    return (
        <div
            className={classes.imageContainer}
            onClick={isGalleryAvailable ? () => setIsGalleryModalOpen(true) : undefined}
        >
            <img className={classes.image} src={additionalMediaEntry?.small_url || url} alt="Garment Image" />
            {isGalleryAvailable && (
                <img className={classes.expandIcon} src={require('../../static/icons/expandGalleryIcon.png')} alt="" />
            )}

            {isGalleryAvailable && (
                <Modal
                    className={classes.galleryModal}
                    type="popup-wide"
                    isOpen={isGalleryModalOpen}
                    onDismiss={onDismiss}
                >
                    <ModalHeader title={productName} closeModal={onDismiss} />
                    <div className={classes.modalContainer}>
                        <ProductGalleryProvider media_entries={displayedMedia}>
                            <ProductGallery
                                name={productName}
                                deliveryIcon={is24HourDelivery}
                                canCustomise={canCustomise}
                                modalGallery
                            />
                        </ProductGalleryProvider>
                    </div>
                </Modal>
            )}
        </div>
    );
};
